import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaFacebookF, FaInstagram, FaPhone, FaEnvelope, FaMapMarkerAlt, FaLeaf, FaShippingFast, FaCertificate } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from './Logo/Logo';

// Import animații
import { fadeInUp, staggerContainer, fadeInLeft, standardViewport } from '../utils/animation';

// Import SCSS - MODULAR: Footer își importă propriul SCSS
import './Footer.scss';

// Constante pentru iconițe
const SOCIAL_LINKS = [
  { icon: <FaFacebookF />, url: 'https://facebook.com', label: 'Facebook' },
  { icon: <FaInstagram />, url: 'https://instagram.com', label: 'Instagram' }
];

const FEATURE_ICONS = [<FaLeaf />, <FaCertificate />, <FaShippingFast />];
const CONTACT_ICONS = [<FaPhone />, <FaEnvelope />, <FaMapMarkerAlt />];

/**
 * Footer component
 * 
 * Componenta pentru footer-ul site-ului care afișează informații de contact,
 * link-uri rapide și link-uri sociale.
 */
const Footer = () => {
  const { t } = useTranslation('common');
  const currentYear = new Date().getFullYear();
  
  const quickLinks = [
    { name: t('navbar.home'), path: '/' },
    { name: t('navbar.about'), path: '/despre-noi' },
    { name: t('navbar.process'), path: '/procesul' },
    { name: t('navbar.contact'), path: '/contact' }
  ];
  
  const features = [
    t('footer.features.natural'),
    t('footer.features.quality'),
    t('footer.features.delivery')
  ];
  
  const contactInfo = [
    '+40 728 969 801',
    'contact@nucartizanal.com',
    'Comuna Coșești, jud. Argeș, sat Lăpușani'
  ];
  
  return (
    <footer className="footer">
      <div className="footer__top">
        <motion.div 
          className="container footer__wave-container"
          initial="hidden"
          whileInView="visible"
          viewport={standardViewport}
          variants={fadeInUp}
        >
          <div className="footer__wave">
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
            </svg>
          </div>
        </motion.div>
      </div>
      
      <div className="container">
        {/* Secțiunea de iconițe - centrată */}
        <motion.div
          className="footer__features"
          initial="hidden"
          whileInView="visible"
          viewport={standardViewport}
          variants={staggerContainer}
        >
          {features.map((feature, index) => (
            <motion.div 
              key={index} 
              className="footer__feature"
              variants={fadeInUp}
            >
              <div className="footer__feature-icon">{FEATURE_ICONS[index]}</div>
              <p>{feature}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      <div className="footer__content container">
        <motion.div 
          className="footer__grid"
          initial="hidden"
          whileInView="visible"
          viewport={standardViewport}
          variants={staggerContainer}
        >
          {/* Prima coloană - Logo și descriere */}
          <motion.div className="footer__column" variants={fadeInLeft}>
            <div className="footer__logo-container">
              <Logo linkTo="/" />
            </div>
            <p className="footer__description">
              {t('footer.description')}
            </p>
            <div className="footer__social-links">
              {SOCIAL_LINKS.map((social, index) => (
                <motion.a 
                  key={index}
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="footer__social-link"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>
          
          {/* A doua coloană - Link-uri rapide */}
          <motion.div className="footer__column" variants={fadeInUp}>
            <h4 className="footer__title">{t('footer.quickLinks')}</h4>
            <div className="footer__links">
              {quickLinks.map((link, index) => (
                <Link key={index} to={link.path} className="footer__link">
                  {link.name}
                </Link>
              ))}
            </div>
          </motion.div>
          
          {/* A treia coloană - Contact */}
          <motion.div className="footer__column" variants={fadeInUp}>
            <h4 className="footer__title">{t('footer.contact')}</h4>
            {contactInfo.map((info, index) => (
              <div key={index} className="footer__contact-info">
                {CONTACT_ICONS[index]}
                <span>{info}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
      
      {/* Copyright */}
      <div className="footer__copyright">
        <div className="container">
          &copy; {currentYear} nucartizanal.com. {t('footer.allRightsReserved')}.
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {}; // Nu sunt necesare props-uri pentru această componentă

export default Footer;
